import { useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { KeyRound, ShieldAlert } from "lucide-react";
import { toast } from "sonner";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { api, formatApiError } from "@/lib/api";

export default function ResetPassword() {
  const [params] = useSearchParams();
  const token = params.get("token") || "";
  const [form, setForm] = useState({ pw: "", confirm: "" });
  const [done, setDone] = useState(false);
  const [busy, setBusy] = useState(false);

  const submit = async () => {
    if (form.pw.length < 6) { toast.error("Password must be at least 6 characters"); return; }
    if (form.pw !== form.confirm) { toast.error("Passwords don't match"); return; }
    setBusy(true);
    try {
      await api.post("/auth/reset-password", { token, new_password: form.pw });
      setDone(true);
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="flex min-h-[70vh] items-center justify-center px-4" data-testid="reset-password-page">
      <div className="glass w-full max-w-sm rounded-xl p-8">
        {!token ? (
          <>
            <ShieldAlert className="mb-4 h-8 w-8 text-neon-magenta" />
            <h1 className="font-display text-2xl font-black uppercase text-white">Invalid Link</h1>
            <p className="mb-6 mt-1 text-sm text-slate-400">This reset link is missing its token. Request a fresh one from the login page.</p>
          </>
        ) : done ? (
          <>
            <KeyRound className="mb-4 h-8 w-8 text-neon-green" />
            <h1 className="font-display text-2xl font-black uppercase text-white">Password Updated</h1>
            <p className="mb-6 mt-1 text-sm text-slate-400" data-testid="reset-success-message">Your new password is set. Sign in with it now.</p>
            <Link to="/admin" data-testid="reset-login-link"
              className="block w-full rounded-md bg-neon-cyan px-4 py-3 text-center font-display text-xs font-extrabold uppercase tracking-wide text-ink transition-colors hover:bg-white">
              Go to Staff Login
            </Link>
          </>
        ) : (
          <>
            <KeyRound className="mb-4 h-8 w-8 text-neon-cyan" />
            <h1 className="font-display text-2xl font-black uppercase text-white">Set New Password</h1>
            <p className="mb-6 mt-1 text-sm text-slate-400">This link works once and expires after 1 hour.</p>
            <Label className="text-xs text-slate-400">New password</Label>
            <Input type="password" value={form.pw} onChange={(e) => setForm({ ...form, pw: e.target.value })}
              data-testid="reset-new-input" className="mb-3 border-white/10 bg-ink text-white" />
            <Label className="text-xs text-slate-400">Confirm new password</Label>
            <Input type="password" value={form.confirm} onChange={(e) => setForm({ ...form, confirm: e.target.value })}
              onKeyDown={(e) => e.key === "Enter" && submit()}
              data-testid="reset-confirm-input" className="mb-5 border-white/10 bg-ink text-white" />
            <button onClick={submit} disabled={busy} data-testid="reset-submit-button"
              className="w-full rounded-md bg-neon-cyan px-4 py-3 font-display text-xs font-extrabold uppercase tracking-wide text-ink transition-colors hover:bg-white disabled:opacity-60">
              {busy ? "Updating..." : "Set Password"}
            </button>
          </>
        )}
        <Link to="/admin" className="mt-4 block text-center font-mono text-xs text-slate-500 transition-colors hover:text-neon-cyan">
          Back to staff login
        </Link>
      </div>
    </div>
  );
}
